interface IHttpOptions {
    cache?: boolean;
    logErrors?: boolean;
}

const timerThreshold = 20000;
const retryCodes = [444, 500, 503, 12002, 12019, 12029, 12030, 12031, 12041, 12152];
const cache = new Map<string, Promise<any>>();

function fetchData<T>(url: string, req, opts: IHttpOptions = {}, isRetry = false): Promise<T> {
    // Default options:
    opts = Object.assign({
        cache: false,
        logErrors: true
    }, opts);
    const startTime = Date.now();

    return fetch(url, req).then(res => {
        const elapsedTime = Date.now() - startTime;
        if (elapsedTime > timerThreshold) {
            logOverThreshold(elapsedTime / 1000, url);
        }

        const contentType = res.headers.get('content-type');
        if (contentType && contentType.startsWith('application/json;')) {
            const promise = res.json();

            if (!res.ok) {
                return promise.then(json => Promise.reject(json));
            }

            if (opts.cache) {
                cache.set(url, promise);
            }

            return promise;
        }

        if (res.ok) {
            return;
        }

        if (!isRetry && retryCodes.indexOf(res.status) !== -1) {
            return fetchData<T>(url, req, opts, true);
        }

        if (opts.logErrors) {
            logError(req, res);
        }

        if (res.status === 401) {
            agentRedirect(res);
        }

        throw new Error(`Non-JSON error: ${res.status} ${res.statusText} ${res.url}`);
    });
}

export function getData<T>(url: string, opts?: IHttpOptions): Promise<T> {
    if (opts && opts.cache) {
        const prom: Promise<T>|undefined = cache.get(url);
        if (prom) {
            return prom;
        }
    }
    return fetchData<T>(url, { credentials: 'same-origin' }, opts);
}

export function postData<T>(url: string, body?, opts?: IHttpOptions): Promise<T> {
    const req = {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
    };
    return fetchData<T>(url, req, opts);
}

function logError(req, res: Response) {
    const { userAgent } = window.navigator;
    if (/googlebot|prerender/i.test(userAgent)) {
        return;
    }

    try {
        const utmId = JSON.parse(document.body.getAttribute('user') || '').UtmId;
        const obj = JSON.stringify({
            utmId,
            userAgent,
            body: req.body
        });
        const msg = `Network error: ${res.status} ${res.statusText} ${req.method} ${res.url} ${obj}`;
        const r = new XMLHttpRequest();
        r.open('POST', 'website/logging/log');
        r.setRequestHeader('Content-Type', 'application/json');
        r.send(JSON.stringify({ LogLevel: 'Warn', Message: msg }));
    } catch (e) {}
}

function logOverThreshold(elapsedTime: number, url: string) {
    try {
        const utm = JSON.parse(document.body.getAttribute('user') || '').UtmId;
        const { userAgent } = window.navigator;
        const msg = `OVERTHRESHOLD | ${elapsedTime}s | ${url} | ${utm} | ${userAgent}`;
        const r = new XMLHttpRequest();
        r.open('POST', 'website/logging/log');
        r.setRequestHeader('Content-Type', 'application/json');
        r.send(JSON.stringify({ LogLevel: 'Warn', Message: msg }));
    } catch (e) {}
}

function agentRedirect(res:Response) {
    const $location = angular.element(document).injector().get('$location');
    const path = $location.path();
    const absUrl = $location.absUrl();
    const redirectUrl = String.format(TRPCONFIG.Domain.AgentLogonUrl, absUrl);

    const header = res.headers.get('www-authenticate');
    if (header && header.indexOf('realm="Agent"') !== -1 && (path !== '/agents/signin')) {
        window.location.href = redirectUrl;
    }
}
